const snippets = [
  {
    id: 'geo-radius',
    title: 'Geolocation Radius Query',
    description: 'Find records within a given radius using the Haversine formula.',
    tags: ['Database', 'Geolocation', 'SQL'],
    variants: [
      {
        lang: 'sql',
        label: 'SQL',
        code: `SELECT
  id,
  name,
  latitude,
  longitude,
  (
    6371 * ACOS(
      COS(RADIANS(:lat)) * COS(RADIANS(latitude))
      * COS(RADIANS(longitude) - RADIANS(:lng))
      + SIN(RADIANS(:lat)) * SIN(RADIANS(latitude))
    )
  ) AS distance_km
FROM locations
HAVING distance_km <= :radius_km
ORDER BY distance_km ASC;`,
      },
      {
        lang: 'php',
        label: 'PHP (Eloquent)',
        code: `Location::selectRaw("
  *,
  (6371 * ACOS(
    COS(RADIANS(?)) * COS(RADIANS(latitude))
    * COS(RADIANS(longitude) - RADIANS(?))
    + SIN(RADIANS(?)) * SIN(RADIANS(latitude))
  )) AS distance_km",
  [$lat, $lng, $lat]
)
->having('distance_km', '<=', $radiusKm)
->orderBy('distance_km')
->get();`,
      },
      {
        lang: 'kotlin',
        label: 'Kotlin (JOOQ)',
        code: `val distance = DSL.field(
    """(6371 * ACOS(
      COS(RADIANS({0})) * COS(RADIANS(latitude))
      * COS(RADIANS(longitude) - RADIANS({1}))
      + SIN(RADIANS({0})) * SIN(RADIANS(latitude))
    ))""",
    Double::class.java,
    lat, lng
)

dsl.select(LOCATIONS.asterisk(), distance)
    .from(LOCATIONS)
    .having(distance.le(radiusKm))
    .orderBy(distance.asc())
    .fetch()`,
      },
    ],
  },
  {
    id: 'email-regex',
    title: 'Email Validation Regex',
    description: 'RFC-aligned email address validation pattern.',
    tags: ['Validation', 'Regex', 'String'],
    variants: [
      {
        lang: 'php',
        label: 'PHP',
        code: `function isValidEmail(string $email): bool
{
    return filter_var($email, FILTER_VALIDATE_EMAIL) !== false;
}

// Or with a raw pattern:
$pattern = '/^[a-zA-Z0-9._%+\\-]+@[a-zA-Z0-9.\\-]+\\.[a-zA-Z]{2,}$/';
$valid = (bool) preg_match($pattern, $email);`,
      },
      {
        lang: 'javascript',
        label: 'JavaScript',
        code: `const EMAIL_REGEX = /^[a-zA-Z0-9._%+\\-]+@[a-zA-Z0-9.\\-]+\\.[a-zA-Z]{2,}$/;

const isValidEmail = (email) => EMAIL_REGEX.test(email);`,
      },
      {
        lang: 'kotlin',
        label: 'Kotlin',
        code: `private val EMAIL_REGEX = Regex(
    """^[a-zA-Z0-9._%+\\-]+@[a-zA-Z0-9.\\-]+\\.[a-zA-Z]{2,}$"""
)

fun isValidEmail(email: String): Boolean = EMAIL_REGEX.matches(email)`,
      },
    ],
  },
  {
    id: 'debounce',
    title: 'Debounce Function',
    description: 'Delay function execution until after a given wait period of inactivity.',
    tags: ['JavaScript', 'Performance', 'UI'],
    variants: [
      {
        lang: 'javascript',
        label: 'JavaScript',
        code: `function debounce(fn, wait) {
  let timer;
  return (...args) => {
    clearTimeout(timer);
    timer = setTimeout(() => fn(...args), wait);
  };
}

// Usage
const onSearch = debounce((query) => fetchResults(query), 300);`,
      },
      {
        lang: 'typescript',
        label: 'TypeScript',
        code: `function debounce<T extends (...args: unknown[]) => void>(
  fn: T,
  wait: number
): (...args: Parameters<T>) => void {
  let timer: ReturnType<typeof setTimeout>;
  return (...args: Parameters<T>) => {
    clearTimeout(timer);
    timer = setTimeout(() => fn(...args), wait);
  };
}

// Usage
const onSearch = debounce((query: string) => fetchResults(query), 300);`,
      },
    ],
  },
  {
    id: 'uuid-v4',
    title: 'UUID v4 Generator',
    description: 'Generate a random UUID v4 identifier without external dependencies.',
    tags: ['Utility', 'String', 'ID'],
    variants: [
      {
        lang: 'php',
        label: 'PHP',
        code: `function uuidV4(): string
{
    return sprintf(
        '%04x%04x-%04x-%04x-%04x-%04x%04x%04x',
        mt_rand(0, 0xffff), mt_rand(0, 0xffff),
        mt_rand(0, 0xffff),
        mt_rand(0, 0x0fff) | 0x4000,
        mt_rand(0, 0x3fff) | 0x8000,
        mt_rand(0, 0xffff), mt_rand(0, 0xffff), mt_rand(0, 0xffff)
    );
}

// Or with ramsey/uuid:
// $uuid = \\Ramsey\\Uuid\\Uuid::uuid4()->toString();`,
      },
      {
        lang: 'javascript',
        label: 'JavaScript',
        code: `// Native — Node 14.17+ and all modern browsers
const uuid = crypto.randomUUID();

// Polyfill for older environments
function uuidV4() {
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
    const r = (Math.random() * 16) | 0;
    return (c === 'x' ? r : (r & 0x3) | 0x8).toString(16);
  });
}`,
      },
      {
        lang: 'go',
        label: 'Go',
        code: `import "github.com/google/uuid"

id := uuid.New().String()
// => "6ba7b810-9dad-11d1-80b4-00c04fd430c8"`,
      },
      {
        lang: 'kotlin',
        label: 'Kotlin',
        code: `import java.util.UUID

val id: String = UUID.randomUUID().toString()
// => "6ba7b810-9dad-11d1-80b4-00c04fd430c8"`,
      },
    ],
  },
  {
    id: 'jwt-decode',
    title: 'JWT Decode (no library)',
    description: 'Decode a JWT payload client-side without any external dependencies.',
    tags: ['Auth', 'JWT', 'JavaScript'],
    variants: [
      {
        lang: 'javascript',
        label: 'JavaScript',
        code: `function decodeJwt(token) {
  const base64 = token.split('.')[1].replace(/-/g, '+').replace(/_/g, '/');
  const json = decodeURIComponent(
    atob(base64)
      .split('')
      .map((c) => '%' + c.charCodeAt(0).toString(16).padStart(2, '0'))
      .join('')
  );
  return JSON.parse(json);
}

const { sub, exp } = decodeJwt(token);`,
      },
      {
        lang: 'typescript',
        label: 'TypeScript',
        code: `interface JwtPayload {
  sub?: string;
  exp?: number;
  iat?: number;
  [key: string]: unknown;
}

function decodeJwt(token: string): JwtPayload {
  const base64 = token.split('.')[1].replace(/-/g, '+').replace(/_/g, '/');
  const json = decodeURIComponent(
    atob(base64)
      .split('')
      .map((c) => '%' + c.charCodeAt(0).toString(16).padStart(2, '0'))
      .join('')
  );
  return JSON.parse(json) as JwtPayload;
}`,
      },
    ],
  },
  {
    id: 'cursor-pagination',
    title: 'Cursor-based Pagination',
    description: 'Stable, performant pagination using a cursor instead of page offsets.',
    tags: ['Database', 'API', 'Performance'],
    variants: [
      {
        lang: 'php',
        label: 'PHP (Eloquent)',
        code: `function paginate(?string $cursor = null, int $limit = 20): array
{
    $query = Item::orderBy('id')->limit($limit + 1);

    if ($cursor) {
        $query->where('id', '>', base64_decode($cursor));
    }

    $items = $query->get();
    $hasMore = $items->count() > $limit;

    if ($hasMore) {
        $items->pop();
    }

    return [
        'data'        => $items,
        'next_cursor' => $hasMore ? base64_encode((string) $items->last()->id) : null,
        'has_more'    => $hasMore,
    ];
}`,
      },
      {
        lang: 'kotlin',
        label: 'Kotlin (JOOQ)',
        code: `data class Page<T>(
    val data: List<T>,
    val nextCursor: String?,
    val hasMore: Boolean,
)

fun paginate(cursor: String? = null, limit: Int = 20): Page<ItemRecord> {
    val decodedCursor = cursor
        ?.let { Base64.getDecoder().decode(it).toString(Charsets.UTF_8).toLong() }

    val rows = dsl.selectFrom(ITEMS)
        .apply { if (decodedCursor != null) where(ITEMS.ID.gt(decodedCursor)) }
        .orderBy(ITEMS.ID.asc())
        .limit(limit + 1)
        .fetch()

    val hasMore = rows.size > limit
    val items = if (hasMore) rows.dropLast(1) else rows
    val nextCursor = if (hasMore) {
        Base64.getEncoder().encodeToString(items.last().id.toString().toByteArray())
    } else null

    return Page(items, nextCursor, hasMore)
}`,
      },
    ],
  },
];

export default snippets;
